import { useState } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import Link from "@/v2-backoffice-client/components/link"
import { DotText } from "@/v2-backoffice-client/components/dot"
import { formatInvoiceAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import {
  deriveSeatModel,
  fitBundle,
  formatBundleSeatPrice,
  formatListPrice,
  formatPriceInterval,
  formatSeatLinePrice,
  licenseWord,
  quoteSeatModel,
  SEAT_MODEL_KIND,
  type SeatModel,
  type SeatModelPlan,
} from "@/v2-backoffice-client/license/plan/license.plan.seatModel.utils"
import type { LicensePlanFragment } from "@/v2-backoffice-client/license/plan/__types__/license.plan.fragment"

const DEFAULT_EXAMPLE_SEATS = 5

export type LicensePlanSeatModelProps = {
  plan: LicensePlanFragment
  /** The full catalog (the board query): a base license's seat line is another row of it. */
  plans: LicensePlanFragment[]
  className?: string
}

// The license panel's "How it's sold" card, first thing on the Revenue tab:
// the list price, how seats are counted and where extra seats are sold, in
// the words a salesperson uses with a customer — then a worked example at
// list price for a team of N. Everything priced here is Fenerum's (the
// mirrored plan terms); the card only spells the shape out. The shape itself
// is the one rule in license.plan.seatModel.utils, the same one the board's
// Seat Model cell follows, so what this card promises is what the
// customer's seat badge will show once the subscriptions exist.
//
// The connection between a base license and its seat line is declared on
// the base (Update License → Seat line) and shown from both sides: the seat
// line on a base, the bases on a seat line. A fixed-price license that names
// no seat line is a seat bundle, and the card shows the product's sizes.
function LicensePlanSeatModel(props: LicensePlanSeatModelProps) {
  const { plan, plans, className } = props
  const model = deriveSeatModel(plan, plans)
  const listPrice = formatListPrice(plan)
  const isPerSeat =
    model.kind === SEAT_MODEL_KIND.SeatLine ||
    model.kind === SEAT_MODEL_KIND.Quantity
  const connectionLabel = connectionLabelOf(model)

  return (
    <section
      className={cn(
        "license-plan-seat-model flex flex-col gap-4 rounded-md border border-border p-4",
        className,
      )}
      data-feature-code="UI_BackofficeLicensePlan_SeatModel"
    >
      <div className="flex items-baseline gap-2">
        <h3 className="text-sm font-semibold">How it&apos;s sold</h3>
        <span className="text-xs text-muted-foreground">
          Fenerum&apos;s list prices; the seat model is set with Update License.
        </span>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 text-sm">
          <dt className="text-muted-foreground">List price</dt>
          <dd className="tabular-nums">
            {listPrice ?? (
              <span className="text-muted-foreground">
                No active term in Fenerum, so nothing is sold on it.
              </span>
            )}
            {listPrice && isPerSeat && model.seatsPerUnit === 1 && (
              <span className="text-muted-foreground"> per seat</span>
            )}
            {listPrice && isPerSeat && model.seatsPerUnit > 1 && (
              <span className="text-muted-foreground">
                {" "}
                per unit of {model.seatsPerUnit} seats
              </span>
            )}
          </dd>

          <dt className="text-muted-foreground">Seats</dt>
          <dd className="min-w-0">
            <SeatModelSeats model={model} />
          </dd>

          {connectionLabel && (
            <>
              <dt className="text-muted-foreground">{connectionLabel}</dt>
              <dd className="min-w-0">
                <SeatModelConnection plan={plan} model={model} />
              </dd>
            </>
          )}
        </dl>

        <SeatModelExample plan={plan} model={model} />
      </div>
    </section>
  )
}

/** The connection row's heading per kind; null when the kind has no connection to show. */
function connectionLabelOf(model: SeatModel) {
  switch (model.kind) {
    case SEAT_MODEL_KIND.Companion:
      return "Seat line"
    case SEAT_MODEL_KIND.Bundle:
      return "Sizes"
    case SEAT_MODEL_KIND.SeatLine:
      return "Serves"
    case SEAT_MODEL_KIND.Flat:
      return model.seatLine || model.hasUnresolvedSeatLine ? "Seat line" : null
    default:
      return null
  }
}

type SeatModelSeatsProps = {
  model: SeatModel
}

// The Seats row: one sentence per kind. The related licenses are not here —
// they have their own row underneath (SeatModelConnection), as a list.
function SeatModelSeats(props: SeatModelSeatsProps) {
  const { model } = props

  switch (model.kind) {
    case SEAT_MODEL_KIND.Companion:
      return (
        <div className="flex flex-col gap-1">
          <span>
            {model.includedSeats > 0
              ? `The price includes ${model.includedSeats} ${model.includedSeats === 1 ? "seat" : "seats"}; `
              : "The price includes no seats; "}
            every extra seat is one unit of the seat line below.
          </span>
          {model.seatLine && model.includedSeats === 0 && (
            <span className="text-xs text-muted-foreground">
              So the quantity on {model.seatLine.Name} counts every user. If
              this price covers the first user, set Seats included to 1.
            </span>
          )}
        </div>
      )
    case SEAT_MODEL_KIND.Bundle:
      return (
        <div className="flex flex-col gap-1">
          {model.includedSeats > 0 ? (
            <span>
              A seat bundle: {model.includedSeats} seats for one price, and that
              is the cap. No per-seat line; a bigger team moves to the next size
              below.
            </span>
          ) : (
            <DotText variant="warning">
              No seats included and no seat line, so customers get no seats. Set
              Seats included (a bundle) or pick a seat line (a base).
            </DotText>
          )}
          {model.includedSeats > 0 &&
            model.includedSeats <= 1 &&
            model.seatLines.length > 0 && (
              <span className="text-xs text-muted-foreground">
                One seat and no seat line: if extra seats are sold per user,
                pick {model.seatLines[0].Name} as the seat line.
              </span>
            )}
        </div>
      )
    case SEAT_MODEL_KIND.SeatLine:
      return (
        <div className="flex flex-col gap-1">
          <span>
            The per-seat line: a customer&apos;s quantity on it is their extra
            seats
            {model.seatsPerUnit > 1
              ? `, ${model.seatsPerUnit} per unit,`
              : ""}{" "}
            on top of what their base includes.
          </span>
          <span className="text-xs text-muted-foreground">
            More seats = a higher quantity on this subscription in Fenerum;
            Backoffice recounts within the hour. A volume price is a contract
            tier on the customer&apos;s account, not another license.
          </span>
        </div>
      )
    case SEAT_MODEL_KIND.Quantity:
      return (
        <span>
          {model.seatsPerUnit > 1
            ? `Sold in units of ${model.seatsPerUnit} seats: a customer who buys 2 has ${2 * model.seatsPerUnit} seats.`
            : `Sold per seat: a customer who buys ${DEFAULT_EXAMPLE_SEATS} has ${DEFAULT_EXAMPLE_SEATS} seats.`}
        </span>
      )
    case SEAT_MODEL_KIND.Flat:
      return (
        <span>
          Company-wide: one price covers the whole company, nobody is counted.
        </span>
      )
    default:
      return (
        <DotText variant="destructive">
          Not set: the license works, customers just get no seat cap. Set the
          seat model with Update License.
        </DotText>
      )
  }
}

type SeatModelConnectionProps = {
  plan: SeatModelPlan
  model: SeatModel
}

// The connection row: on a base, the seat line it names (one line, with
// its price and how many other licenses share it); on a seat line, the
// bases that name it as a small table; on a bundle, the product's sizes;
// on a company-wide license, the seat line whose seats still count. Then
// one line on where the connection is made.
function SeatModelConnection(props: SeatModelConnectionProps) {
  const { plan, model } = props
  const productName = plan.ProductName ?? "this product"

  if (model.kind === SEAT_MODEL_KIND.SeatLine) {
    return (
      <div className="flex flex-col gap-2">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="pb-1 pr-3 font-medium">License</th>
              <th className="pb-1 pr-3 text-right font-medium">List price</th>
              <th className="pb-1 pr-3 text-right font-medium">Included</th>
              <th className="pb-1 text-right font-medium">Customers</th>
            </tr>
          </thead>
          <tbody>
            {model.basePlans.map((basePlan) => (
              <tr key={basePlan.Id} className="border-t border-border/60">
                <td className="py-1 pr-3">
                  <SeatModelPlanLink plan={basePlan} />
                </td>
                <td className="py-1 pr-3 text-right tabular-nums">
                  {formatListPrice(basePlan) ?? (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="py-1 pr-3 text-right tabular-nums">
                  {basePlan.IncludedSeats ?? 0}
                </td>
                <td className="py-1 text-right tabular-nums">
                  {basePlan.CustomerCount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <span className="text-xs text-muted-foreground">
          Each names this license as its seat line (its own Update License).
          While they do, this license stays per seat on {productName}.
        </span>
      </div>
    )
  }

  if (model.kind === SEAT_MODEL_KIND.Bundle) {
    return (
      <div className="flex flex-col gap-2">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="pb-1 pr-3 font-medium">Size</th>
              <th className="pb-1 pr-3 text-right font-medium">Seats</th>
              <th className="pb-1 pr-3 text-right font-medium">List price</th>
              <th className="pb-1 pr-3 text-right font-medium">Per seat</th>
              <th className="pb-1 text-right font-medium">Customers</th>
            </tr>
          </thead>
          <tbody>
            {model.bundles.map((bundle) => {
              const isCurrent = bundle.Id === plan.Id
              return (
                <tr
                  key={bundle.Id}
                  className={cn(
                    "border-t border-border/60",
                    isCurrent && "bg-muted/40 font-medium",
                  )}
                  aria-current={isCurrent ? "true" : undefined}
                >
                  <td className="py-1 pr-3">
                    {isCurrent ? (
                      bundle.Name
                    ) : (
                      <SeatModelPlanLink plan={bundle} />
                    )}
                  </td>
                  <td className="py-1 pr-3 text-right tabular-nums">
                    {bundle.IncludedSeats ?? 0}
                  </td>
                  <td className="py-1 pr-3 text-right tabular-nums">
                    {formatListPrice(bundle) ?? (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="py-1 pr-3 text-right tabular-nums">
                    {formatBundleSeatPrice(bundle) ?? (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="py-1 text-right tabular-nums">
                    {bundle.CustomerCount}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <span className="text-xs text-muted-foreground">
          The {productName} licenses sold as seat bundles: fixed price, seats
          included, no seat line. A customer holds one size at a time and moves
          up in Fenerum.
        </span>
      </div>
    )
  }

  const seatLine = model.seatLine

  if (!seatLine) {
    return (
      <DotText variant="warning">
        This license names a seat line that is no longer in the catalog. Pick
        the {productName} per-seat license it sells extra seats on with Update
        License.
      </DotText>
    )
  }

  const seatLinePrice = formatSeatLinePrice(seatLine)
  // The other licenses of the product selling their extra seats on this line.
  const sharedCount = model.basePlans.length
  return (
    <div className="flex flex-col gap-2">
      <span className="flex flex-wrap items-baseline gap-x-2">
        <SeatModelPlanLink plan={seatLine} />
        {seatLinePrice && (
          <span className="tabular-nums text-muted-foreground">
            {seatLinePrice}
          </span>
        )}
        {model.kind === SEAT_MODEL_KIND.Flat ? (
          <span className="text-xs text-muted-foreground">
            seats a customer buys here still count toward their cap
          </span>
        ) : sharedCount > 0 ? (
          <span className="text-xs text-muted-foreground">
            shared with {sharedCount} other {productName}{" "}
            {licenseWord(sharedCount)}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">
            no other {productName} license sells its seats here
          </span>
        )}
      </span>
      {seatLine.DeprecatedAt && (
        <DotText variant="warning">
          {seatLine.Name} is deprecated: new customers of this license have
          nowhere to buy extra seats until another seat line is named.
        </DotText>
      )}
      {model.seatLines.length > 1 && (
        <span className="text-xs text-muted-foreground">
          {productName} has {model.seatLines.length} per-seat licenses (
          {model.seatLines.map((candidate) => candidate.Name).join(", ")}
          ). Seats bought on one this license does not name still count, and the
          customer row flags them.
        </span>
      )}
      <span className="text-xs text-muted-foreground">
        Set on this license (Update License → Seat line). Clear it to sell this
        license as a seat bundle instead.
      </span>
    </div>
  )
}

function SeatModelPlanLink(props: { plan: SeatModelPlan }) {
  const { plan } = props
  // Push, so the breadcrumb leads back to the license this was read from.
  return (
    <Link to={`>>?v=plan:${plan.Id}`} iconSrc={null} className="font-medium">
      {plan.Name}
    </Link>
  )
}

type SeatModelExampleProps = {
  plan: SeatModelPlan
  model: SeatModel
}

// The worked example: a team of N at list price, line by line. Only the
// shapes that have a price arithmetic get one (a base with its seat line,
// a seat bundle, a per-seat license); the rest say why not.
function SeatModelExample(props: SeatModelExampleProps) {
  const { plan, model } = props
  const [seatsText, setSeatsText] = useState(String(DEFAULT_EXAMPLE_SEATS))
  const seats = Number(seatsText)
  const isValid = seatsText !== "" && Number.isInteger(seats) && seats >= 1
  const quote = isValid ? quoteSeatModel({ plan, model, seats }) : null

  const hasArithmetic =
    model.kind === SEAT_MODEL_KIND.Companion ||
    model.kind === SEAT_MODEL_KIND.Bundle ||
    model.kind === SEAT_MODEL_KIND.Quantity

  if (!hasArithmetic) {
    return (
      <div className="flex flex-col gap-1 self-start rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Worked example</span>
        {model.kind === SEAT_MODEL_KIND.SeatLine
          ? "Open one of the licenses it serves to see what a team pays."
          : model.kind === SEAT_MODEL_KIND.Flat
            ? "A company-wide license costs its list price whatever the team size."
            : "Set the seat model first."}
      </div>
    )
  }

  const largestBundle = model.bundles[model.bundles.length - 1]
  const nextBundle =
    quote?.bundle != null
      ? model.bundles[model.bundles.indexOf(quote.bundle) + 1]
      : undefined

  return (
    <div className="flex flex-col gap-2 self-start rounded-md bg-muted/40 px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor="licensePlanSeatModelSeats" className="text-xs">
          Worked example, team of
        </Label>
        <Input
          id="licensePlanSeatModelSeats"
          type="number"
          min={1}
          className="h-7 w-20 text-right tabular-nums"
          value={seatsText}
          onChange={(e) => {
            setSeatsText(e.target.value)
          }}
        />
      </div>

      {quote ? (
        <table className="w-full text-xs">
          <tbody>
            {quote.lines.map((line) => (
              <tr key={line.label}>
                <td className="py-0.5 pr-2">
                  {line.label}
                  <span className="text-muted-foreground">
                    {" "}
                    × {line.quantity} at {formatNumber(line.unitPrice)}
                  </span>
                </td>
                <td className="py-0.5 text-right tabular-nums">
                  {formatNumber(line.amount)}
                </td>
              </tr>
            ))}
            <tr className="border-t border-border font-medium">
              <td className="pt-1 pr-2">At list price{quote.interval}</td>
              <td className="pt-1 text-right tabular-nums">
                {formatInvoiceAmount(quote.total, quote.currency)}
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Icon src="info" size={12} className="mt-0.5 shrink-0" aria-hidden />
          <span>
            {!isValid
              ? "Enter a team size of 1 or more."
              : model.kind === SEAT_MODEL_KIND.Bundle
                ? fitBundle(model, seats) == null
                  ? largestBundle
                    ? `Beyond the largest size (${largestBundle.Name}, ${largestBundle.IncludedSeats ?? 0} seats): price by agreement.`
                    : "No size has a seat count to fit the team on."
                  : "The size the team fits has no list price to quote from."
                : plan.ListPrice == null
                  ? "No list price to quote from."
                  : model.kind === SEAT_MODEL_KIND.Companion && !model.seatLine
                    ? "No seat line to price the extra seats on."
                    : "The seat line has no list price in this currency."}
          </span>
        </p>
      )}

      <p className="text-xs text-muted-foreground">
        {quote?.bundle
          ? `${quote.bundle.Name} covers up to ${quote.bundle.IncludedSeats ?? 0} seats${
              formatBundleSeatPrice(quote.bundle)
                ? ` (${formatBundleSeatPrice(quote.bundle)})`
                : ""
            }${
              nextBundle
                ? `; the next size is ${nextBundle.Name}, ${nextBundle.IncludedSeats ?? 0} seats${
                    formatListPrice(nextBundle)
                      ? ` at ${formatListPrice(nextBundle)}`
                      : ""
                  }`
                : "; it is the largest size"
            }. `
          : quote?.isMonthlyEquivalent
            ? `${model.seatLine?.Name ?? "The seat line"} sells no ${formatPriceInterval(plan.ListPriceIntervalType, plan.ListPriceIntervalCount).slice(1)} term, so both prices are shown per month. `
            : ""}
        List price before any discount or contract agreed in Fenerum; the
        customer panel shows what is billed.
      </p>
    </div>
  )
}

export default LicensePlanSeatModel
